import { useState } from 'react'

/**
 * The Insuronix brand lockup.
 *
 * The supplied artwork is a horizontal lockup (425x116, ~3.66:1) that already
 * contains BOTH the "insuronix" wordmark and the "powered by Arivonix AI"
 * line. So wherever it is used it replaces the prototype's separate mark +
 * wordmark (+ tagline) rather than sitting beside them - otherwise that text
 * would appear twice.
 *
 * Served from `frontend/public/logo.png` rather than bundled, so a missing
 * file is a recoverable 404 rather than a build error; `onError` falls back to
 * the prototype's letter mark and text.
 *
 *   variant="sidebar"  full lockup, collapsing to a square mark on the 68px rail
 *   variant="auth"     full lockup, centred (login / signup)
 *   variant="setup"    full lockup, centred (onboarding source picker)
 *   variant="mark"     just the square letter tile, for tight inline spots
 */

export type LogoVariant = 'sidebar' | 'auth' | 'setup' | 'mark'

const LOGO_SRC = '/logo.png'
const MARK_SRC = '/logo-mark.png'
const ALT = 'Insuronix - powered by Arivonix AI'

export function LogoMark({ variant = 'sidebar', markClass }: { variant?: LogoVariant; markClass?: string }) {
  const [failed, setFailed] = useState(false)

  // A tight inline badge: never the wide lockup.
  if (variant === 'mark') {
    return <div className={markClass ?? 'ix-sidebar__mark'}>I</div>
  }

  if (failed) {
    // Prototype fallback, per surface.
    if (variant === 'sidebar') {
      return (
        <>
          <div className="ix-sidebar__brand-row">
            <div className="ix-sidebar__mark">I</div>
            <div className="ix-sidebar__wordmark">insuronix</div>
          </div>
          <div className="ix-sidebar__tagline">powered by Arivonix AI</div>
        </>
      )
    }
    const cls = variant === 'auth' ? 'au' : 'su'
    return (
      <>
        <div className={`${cls}-mark`}>I</div>
        <div className={`${cls}-word`}>insuronix</div>
      </>
    )
  }

  if (variant === 'sidebar') {
    return (
      <>
        <img className="ix-brand-logo" src={LOGO_SRC} alt={ALT} onError={() => setFailed(true)} />
        {/* Shown only when the rail is collapsed to 68px, where the wide
            lockup would be illegible. */}
        <img className="ix-brand-mark" src={MARK_SRC} alt="" aria-hidden="true" />
      </>
    )
  }

  return (
    <img
      className={`ix-brand-logo ix-brand-logo--${variant}`}
      src={LOGO_SRC}
      alt={ALT}
      onError={() => setFailed(true)}
    />
  )
}

export default LogoMark
